import DateService, { ISO_8601 } from '@services/DateService'
import type { Model as ServiceModel } from './api/Model'
import { Variable } from './Variable'
import { Level } from './Level'
import { OverlayVariable } from './overlay/OverlayVariable'
import { OverlayLevel } from './overlay/OverlayLevel'
import i18n from 'src/i18n'
import dayjs, { Dayjs } from 'dayjs'
import { Extent } from './api/Extent'
import { Hours, Minutes } from 'src/typings/time'
import { RepresentationType } from './RepresentationType'
import { toRaw } from 'vue'

export enum ModelID {
  FAKE = 'X',
  GFS_025_EUROPA = 'GFS_025_EUROPA',
  WRF_d01 = 'WRF_d01',
  WRF_d02 = 'WRF_d02',
  WRF_d03 = 'WRF_d03',
  WRF_d04 = 'WRF_d04'
}

export class Model {
  id: ModelID
  name: string
  label: string
  out: string
  outDate: Dayjs
  visible: boolean
  extent: L.BoundsLiteral
  exportable: boolean
  timeInterval: Minutes
  timeWindow: Hours
  variables?: Variable[]
  attribution: string
  isRadiosondeoEnabled: boolean
  isMeteogramEnabled: boolean
  isReducedModeCompatible?: boolean
  previousOuts?: Dayjs[]
  isMonthly?: boolean
  modelDatesAvailable?: Map<string, Dayjs[]> = new Map()

  constructor(
    serviceModel: ServiceModel,
    isNoLevelsAllowed: boolean,
    isNoDateAllowed: boolean,
    public groupId: string,
    public isBackInTime: boolean = false
  ) {
    this.id = serviceModel.ID as ModelID // || serviceModel.NAME // TODO: Sustituir ID por REFER, pero me da miedo que pete algo
    this.name = serviceModel.NAME
    this.label = serviceModel.NAME
    this.out = serviceModel.SALIDA
    if (!this.out) {
      this.outDate = DateService.utc(serviceModel.DATE, 'YYYY-MM-DD')
    } else {
      this.outDate = DateService.utc(serviceModel.DATE, 'YYYY-MM-DD')?.hour(
        +this.out
      )!
    }
    this.previousOuts = serviceModel.PREVIOUS_OUTS?.map((out) =>
      dayjs.utc(out, ISO_8601)
    )
    this.exportable = serviceModel.EXPORTABLE
    this.timeInterval = serviceModel.TIME_INTERVAL
    this.timeWindow = serviceModel.TIME_WINDOW
    this.attribution = serviceModel.ATRIBUTION
    this.visible = !serviceModel.DISABLE
    this.isRadiosondeoEnabled = serviceModel.ENABLE_RADIOSONDEO
    this.isMeteogramEnabled = serviceModel.ENABLE_METEOGRAM
    this.isMonthly = this.timeInterval >= 1440 * 28

    if (Array.isArray(serviceModel.EXTENT)) {
      this.extent = serviceModel.EXTENT as any
    } else {
      this.extent = Extent.toLeafletExtent(serviceModel.EXTENT)!
    }
    // this.extent = toLeafletExtent(serviceModel.EXTENT)!

    if (serviceModel.VARIABLES) {
      this.variables = serviceModel.VARIABLES.map(
        (serviceVariable) =>
          new Variable(serviceVariable, this, isNoLevelsAllowed)
      )
    }

    if (!this.outDate && !isNoDateAllowed) {
      this.visible = false
    }
    this.isReducedModeCompatible = this.variables?.some(
      (variable) => variable.isReducedModeCompatible
    )
  }

  hasDate() {
    return !!this.outDate
  }

  getDate() {
    let date = this.outDate
    if (Object.is(date, NaN)) {
      date = DateService.now()
    }
    return date ?? DateService.now()
  }

  getDatesAvailableFromCache() {
    if (this.modelDatesAvailable) {
      const datesSaved = this.modelDatesAvailable.get(
        this.outDate.toISOString()
      )
      if (datesSaved?.length) {
        return datesSaved
      }
    }
  }

  getDatesAvailable() {
    const datesFromCache = this.getDatesAvailableFromCache()
    if (datesFromCache) {
      return datesFromCache
    }
    const stepHours: Hours = this.timeInterval / 60
    let dates: Dayjs[]
    if (this.isBackInTime) {
      dates = DateService.getPastDates(this.outDate, this.timeWindow, stepHours)
    } else {
      dates = DateService.getFutureDates(
        this.outDate,
        this.timeWindow,
        stepHours
      )
    }
    this.modelDatesAvailable?.set(this.outDate.toISOString(), dates)
    return dates
  }

  getVariable(referenceVariable: string) {
    return (
      this.variables &&
      this.variables?.find((x) => x.reference === referenceVariable)
    )
  }

  hasVariable(idVariable: string) {
    return !!this.getVariable(idVariable)
  }

  getVariablesNotOverlayFGB() {
    if (!this.variables) return [] // Early exit if variables is null or undefined
    const variables = [...toRaw(this.variables)]

    const variablesNotOverlay: Variable[] = []

    for (const v of variables) {
      const levelsNotOverlay: Level[] = []

      for (const l of v.levels) {
        if (
          !l.isOverlable &&
          l.representation == RepresentationType.FLAT_GEOBUFF
        )
          levelsNotOverlay.push(l)
      }
      v.levels = levelsNotOverlay

      if (v.levels.length) variablesNotOverlay.push(v)
    }
    return variablesNotOverlay
  }

  getOverlayVariable(): OverlayVariable | undefined {
    const overlayLevels = this.getOverlayLevels()
    if (overlayLevels.length) {
      return {
        icon: 'opacity',
        id: 'SWITCH',
        name: i18n.t('overlapedVariables'),
        levels: overlayLevels.map((overlay) => overlay.level)
      }
    }
  }

  isModelActual() {
    return this.id === 'X'
  }

  getOverlayLevels(): OverlayLevel[] {
    const overlayLevels: Level[] = []
    this.variables?.forEach((variable) => {
      overlayLevels.push(
        ...variable.levels.filter((level) => level.isOverlable)
      )
    })
    return overlayLevels.map((level) => level.toOverlayLevel(this))
  }

  getEquivalentOverlayLevel(overlay: OverlayLevel) {
    const overlayLevels = this.getOverlayLevels()
    const equivalentLevel = overlayLevels.find((x) => {
      return x.transversalId === overlay.level.transversalId
    })
    return equivalentLevel
  }

  getOutHourInLocalTime() {
    return DateService.getHourInLocalTime(Number(this.out))
  }

  hasSomeVariableInPersonalizedGroupSelected() {
    return this.variables?.some((variable) =>
      variable.hasLevelsInSelectedPersonalizedGroup()
    )
  }

  getEquivalentVariable(variable: Variable) {
    return this.variables?.find((x) => x.reference === variable.reference)
  }
}
